import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { WorkspaceService } from "@gen/tracker/v1/workspace_connect";
import { UpdateWorkspaceRequest, type Workspace } from "@gen/tracker/v1/workspace_pb";
import { DEFAULT_REPORT_TZ } from "@/lib/period";
import { idChunks } from "@/lib/services/catalog";

const client = createClient(WorkspaceService);

export const workspaceKeys = {
  all: ["workspace"] as const,
  own: () => [...workspaceKeys.all, "own"] as const,
  people: (ids: string[]) => [...workspaceKeys.all, "people", ids] as const,
};

/**
 * Names for the people behind ids a screen shows — an owner, an author, an
 * actor — from the realm's directory, whether or not they ever signed in
 * here. An id the directory does not know is absent; show the id then.
 */
export function usePeopleNames(ids: Iterable<string | undefined>) {
  const unique = idChunks([...ids].filter((v): v is string => !!v)).flat();
  return useQuery({
    queryKey: workspaceKeys.people(unique),
    queryFn: async (): Promise<Record<string, string>> => {
      const out: Record<string, string> = {};
      for (const chunk of idChunks(unique)) {
        const res = await client.getPeopleNames({ ids: chunk });
        Object.assign(out, res.names);
      }
      return out;
    },
    enabled: unique.length > 0,
    staleTime: 5 * 60_000,
  });
}

/** One person's name, else the id itself. */
export function usePersonName(id: string | undefined): string {
  const { data } = usePeopleNames([id]);
  return (id && data?.[id]) || id || "";
}

/** Loads the caller's own workspace (empty id → resolved server-side). */
export function useGetWorkspace() {
  return useQuery({
    queryKey: workspaceKeys.own(),
    queryFn: async (): Promise<Workspace | undefined> => {
      const res = await client.getWorkspace({ id: "" });
      return res.workspace;
    },
    staleTime: 60_000,
  });
}

/**
 * The zone every day of every report is counted in: the workspace's
 * (Settings → Workspace), the server's default until it has loaded.
 */
export function useReportTimezone(): string {
  const { data } = useGetWorkspace();
  return data?.timezone || DEFAULT_REPORT_TZ;
}

export interface WorkspaceSettingsForm {
  name: string;
  defaultTrackingDomainId: string; // "" clears the default
  defaultGoalPreset: string; // goal preset key; "" = none
  /** IANA zone every report counts its days in; "" leaves it unchanged. */
  timezone?: string;
}

export function toUpdateWorkspaceRequest(form: WorkspaceSettingsForm): UpdateWorkspaceRequest {
  return new UpdateWorkspaceRequest({
    id: "",
    name: form.name.trim(),
    defaultTrackingDomainId: form.defaultTrackingDomainId,
    defaultGoalPreset: form.defaultGoalPreset,
    timezone: form.timezone ?? "",
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (form: WorkspaceSettingsForm) => {
      const res = await client.updateWorkspace(toUpdateWorkspaceRequest(form));
      return res.workspace;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
}
